import t from 'react-translate'
import { useSelector } from 'react-redux';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { config } from '@config/pendo.config.json';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import { Button } from 'react-bootstrap';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import { getCsatQuestions, getNpsQuestions, getNpsCsatConfiguration } from 'redux/selectors/analytics';
import { addNpsCsatConfiguration, updateNpsCsatConfiguration } from 'redux/actions/analytics';
import { NpsCsatConfigRequest } from 'redux/schemas/api/analytics';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { AngularServicesContext } from 'react-app';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useAppDispatch } from 'redux/store';

interface NpsCsatConfigurationFormData {
  npsQuestionId: NvDropdownOption | null;
  csatQuestionId: NvDropdownOption | null;
}

const schema = yup.object().shape({
  npsQuestionId: yup.mixed().nullable(),
  csatQuestionId: yup.mixed().nullable(),
}).test(
  'at-least-one-selected',
  'Please select at least one question.',
  (values) => {
    const npsSelected = !!values.npsQuestionId;
    const csatSelected = !!values.csatQuestionId;
    return npsSelected || csatSelected;
  }
) as yup.Schema<NpsCsatConfigurationFormData>;


const NpsCsatConfigurationModal = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useAppDispatch();
  const { $state } = useContext(AngularServicesContext);
  const { catalogId, questionId } = $state?.params;
  const institution = useSelector(getCurrentInstitution);
  const npsCsatConfig = useSelector(getNpsCsatConfiguration);
  const isConfigured = Boolean(npsCsatConfig?.configured);
  const npsQuestions = useSelector(getNpsQuestions);
  const csatQuestions = useSelector(getCsatQuestions);
  const [saving, setSaving] = useState(false);
  const methods = useForm<NpsCsatConfigurationFormData>({
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      npsQuestionId: null,
      csatQuestionId: null,
    },
  });
  const { handleSubmit, formState } = methods;
  const onSubmit = async (data: NpsCsatConfigurationFormData) => {
    setSaving(true);
    const submitAction = isConfigured ? updateNpsCsatConfiguration : addNpsCsatConfiguration;
    const requestData: NpsCsatConfigRequest = {
      institutionId: institution!.id,
      catalogId: catalogId,
      questionSetId: +questionId!,
      npsQuestionId: data?.npsQuestionId?.id ? +data.npsQuestionId.id : null,
      csatQuestionId: data?.csatQuestionId?.id ? +data.csatQuestionId.id: null,
    };
    try {
      await dispatch(submitAction(requestData)).unwrap();
      onClose();
    } catch (error) {
      // Error alert already handled in redux action
      console.error('Error saving NPS/CSAT configuration', error);
    } finally {
      setSaving(false);
    }
  }

  const npsOptions: NvDropdownOption[] = useMemo(
    () => npsQuestions?.map((question) => ({
      type: 'text',
      id: question.id,
      value: question.id,
      text: question.questionText,
      class: 'text-wrap',
    })),
    [npsQuestions]
  );
  const csatOptions: NvDropdownOption[] = useMemo(
    () => csatQuestions?.map((question) => ({
      type: 'text',
      id: question.id,
      value: question.id,
      text: question.questionText,
      class: 'text-wrap',
    })),
    [csatQuestions]
  );

  useEffect(() => {
    if (npsCsatConfig) {
      const { npsQuestionId, csatQuestionId } = npsCsatConfig;
      methods.reset({
        npsQuestionId: npsOptions?.find(option => option.id === npsQuestionId) || null,
        csatQuestionId: csatOptions?.find(option => option.id === csatQuestionId) || null,
      });
    }
  }, [npsCsatConfig, npsOptions, csatOptions]);

  return (
    <NvModal
      show
      type={ModalType.DYNAMIC}
      onClose={onClose}
      header={t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.MODAL_TITLE()}
      body={(
        <div className='d-flex flex-column p-4 text-large-body'>
          <div className='mb-4 bold'>
            {t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.MODAL_DESCRIPTION()}
          </div>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {npsOptions && npsOptions.length > 0 && (
                <div className='mb-4'>
                  <div className='mb-1 bold'>{t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.NPS_QUESTION()}</div>
                  <NvFormDropdown
                    items={npsOptions}
                    name='npsQuestionId'
                    isSmallSize
                    className='my-2'
                    title={t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.NPS_QUESTION_PLACEHOLDER()}
                  />
                </div>
              )}
              {csatOptions && csatOptions.length > 0 && (
                <div className='mb-4'>
                  <div className='mb-1 bold'>{t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.CSAT_QUESTION()}</div>
                  <NvFormDropdown
                    items={csatOptions}
                    name='csatQuestionId'
                    isSmallSize
                    className='my-2'
                    title={t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.CSAT_QUESTION_PLACEHOLDER()}
                  />
                </div>
              )}
              <div className='d-flex justify-content-center mt-5'>
                <Button
                  variant='secondary'
                  type='button'
                  className='mx-1'
                  onClick={() => onClose()}
                >
                  {t.FORM.SKIP()}
                </Button>
                <Button
                  variant='primary'
                  type='submit'
                  className='mx-1'
                  disabled={!formState.isValid || saving}
                >
                  {saving ? t.FORM.SAVING() : t.FORM.SAVE()}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      )}
      width={800}
      closeDataQa={config.pendo.analyticsDashboard.prePost.setupModal.close}
    />
  );
};

export default NpsCsatConfigurationModal;
